import { TagTypes } from "../../../Api/Types/tagTypes";
import { cleanParams } from "../../Shared/Helpers/cleanParams";
import { PagedList } from "../../Shared/Types/common";
import { ImageDto } from "../../Images/Types/imageTypes";
import {
	CloneOfferRequest,
	CloneOfferResponse,
	Offer,
	OfferFiltersDto,
	OfferListRequest,
} from "../Types/offerTypes";

import { offersApi } from "./offersApi";

const extendedOffersApi = offersApi.injectEndpoints({
	// Ensure this is set to true to override existing endpoints
	endpoints: (builder) => ({
		getOffersList: builder.query<Offer[], void>({
			query: () => "/offers/list",
			providesTags: [{ type: TagTypes.Offer, id: "List" }],
		}),
		getOffersListNew: builder.query<PagedList<Offer>, OfferListRequest>({
			query: (params) => ({
				url: "/offers/search",
				params: cleanParams(params),
			}),
			providesTags: [{ type: TagTypes.Offer, id: "List" }],
		}),
		getOffersFilterSelects: builder.query<OfferFiltersDto, void>({
			query: () => "/offers/filters",
			providesTags: [{ type: TagTypes.Offer, id: "List" }],
		}),
		getOfferWithAssociationsById: builder.query<Offer, number>({
			query: (id) => `/offers/with-associations/${id}`,
			providesTags: (result, error, id) => [{ type: TagTypes.Offer, id }],
		}),
		cloneOffer: builder.mutation<CloneOfferResponse, { sourceOfferId: number; data: CloneOfferRequest }>({
			query: ({ sourceOfferId, data }) => ({
				url: `/offers/${sourceOfferId}/clone`,
				method: 'POST',
				body: data,
			}),
			invalidatesTags: [{ type: TagTypes.Offer, id: 'List' }],
		}),
		getOfferImages: builder.query<ImageDto[], number>({
			query: (offerId) => `/images/Offer/${offerId}`,
			providesTags: (result, error, offerId) => [
				{ type: TagTypes.OfferImages, id: offerId },
			],
		}),
		uploadOfferImages: builder.mutation<{ imageIds: string[]; count: number }, { offerId: number; files: File[] }>({
			query: ({ offerId, files }) => {
				const formData = new FormData();
				files.forEach((file) => formData.append('Files', file));
				formData.append('Category', 'Offer');
				formData.append('EntityId', offerId.toString());
				return {
					url: '/images/upload-multiple',
					method: 'POST',
					body: formData,
				};
			},
			invalidatesTags: (result, error, { offerId }) => [
				{ type: TagTypes.OfferImages, id: offerId },
			],
		}),
		deleteOfferImage: builder.mutation<void, { imageId: string; offerId: number }>({
			query: ({ imageId }) => ({
				url: `/images/${imageId}`,
				method: 'DELETE',
			}),
			invalidatesTags: (result, error, { offerId }) => [
				{ type: TagTypes.OfferImages, id: offerId },
				{ type: TagTypes.Offer, id: offerId },
			],
		}),
		setOfferMainImage: builder.mutation<void, { offerId: number; imageId: string }>({
			query: ({ offerId, imageId }) => ({
				url: `/offers/${offerId}/main-image`,
				method: 'PUT',
				body: { imageId },
			}),
			invalidatesTags: (result, error, { offerId }) => [
				{ type: TagTypes.Offer, id: offerId },
				{ type: TagTypes.OfferImages, id: offerId },
			],
		}),
		removeOfferMainImage: builder.mutation<void, number>({
			query: (offerId) => ({
				url: `/offers/${offerId}/main-image`,
				method: 'DELETE',
			}),
			invalidatesTags: (result, error, offerId) => [
				{ type: TagTypes.Offer, id: offerId },
				{ type: TagTypes.OfferImages, id: offerId },
			],
		}),
	}),

	overrideExisting: false,
});

export const {
	useGetOffersListQuery,
	useGetOffersFilterSelectsQuery,
	useGetOffersListNewQuery,
	useGetOfferWithAssociationsByIdQuery,
	useCloneOfferMutation,
	useGetOfferImagesQuery,
	useUploadOfferImagesMutation,
	useDeleteOfferImageMutation,
	useSetOfferMainImageMutation,
	useRemoveOfferMainImageMutation,
} = extendedOffersApi;
